import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { concat, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { isEqual } from 'lodash-es';

@Component({
  selector: 'auto-select',
  templateUrl: './auto-select.component.html',
  styleUrls: ['./auto-select.component.scss'],
})
export class AutoSelectComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public formGroupClass: string = '';
  @Input() public ngSelectClass: string | 'custom-billing-dropdown' = '';
  @Input() public label: string;
  @Input() public infoTooltip: string = '';
  @Input() public labelClass: string = '';
  @Input() public requiredLabel: boolean = false;
  @Input() public bindValue: string;
  @Input() public bindLabel: string | string[];
  @Input() public bindOption: string | string[];
  @Input() public change: (temp: string) => Observable<any[]>;
  @Input() public fc: UntypedFormControl = new UntypedFormControl(null);
  @Input() public initializeItems: any[] = [];
  @Input() public items: any[] = [];
  @Input() public updateCountCharacter: number = 1;
  @Input() public clearable: boolean = true;
  @Input() public dropdownPosition: string = 'bottom';
  @Input() public errors: any[] = [];
  @Input() public helper: string = '';
  @Input() public placeholder: string = '';
  @Input() public multiple: boolean = false;
  @Input() public closeOnSelect: boolean = true;
  @Input() public hideSelected: boolean = true;
  @Input() public searchOnOpen: boolean = false;
  @Input() public isShowHelper: boolean = true;
  @Input() public searchable: boolean = true;
  @Input() public optionTemplate: TemplateRef<any>;
  @Output() public update: EventEmitter<any> = new EventEmitter();
  @Output() public clear: EventEmitter<void> = new EventEmitter();
  @Output() public remove: EventEmitter<any> = new EventEmitter();

  public isLoading: boolean = false;
  public options: string[] = [];
  public labels: string[] = [];
  public isBindLabelArray: boolean = false;
  public input$: Subject<string> = new Subject<string>();
  public items$: Observable<any[]>;
  private isDestroy: ReplaySubject<boolean> = new ReplaySubject();
  constructor() {}

  public ngOnInit(): void {
    this.searchItems();
  }

  public ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.bindLabel) {
      this.isBindLabelArray = Array.isArray(this.bindLabel);
      this.setLabels();
    }
    if (simpleChanges.bindOption || simpleChanges.bindLabel) {
      this.setOptions();
    }

    if (simpleChanges.initializeItems) {
      this.searchItems();
    }
  }

  public setLabels() {
    if (this.bindLabel) {
      this.labels = Array.isArray(this.bindLabel) ? this.bindLabel : [this.bindLabel];
    } else {
      this.labels = [];
    }
  }

  public setOptions() {
    if (this.bindOption) {
      this.options = Array.isArray(this.bindOption) ? this.bindOption : [this.bindOption];
    } else {
      this.options = this.labels;
    }
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public openEvent() {
    if (this.searchOnOpen) {
      this.input$.next('');
    }
  }

  public onChangeData(val: any) {
    this.update.emit(val);
  }

  public onClear() {
    this.clear.emit();
  }

  public onRemove(val: any) {
    this.remove.emit(val);
  }

  public removeItem<T>(item: T) {
    const ports: T[] = this.fc.value;
    this.fc.patchValue(ports.filter((i) => !isEqual(i, item)));
  }

  private searchItems() {
    this.items$ = concat(
      of(this.initializeItems || []),
      this.input$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.isLoading = true)),
        switchMap((term) =>
          (term || '').length < this.updateCountCharacter
            ? this.emptyData()
            : this.change((term || '').trim()).pipe(
                catchError(() => this.emptyData()),
                tap(() => (this.isLoading = false)),
              ),
        ),
      ),
    );
  }

  private emptyData(): Observable<any[]> {
    this.isLoading = false;
    return of([]);
  }
}
